import { useContext, useState, useEffect } from "react";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Image } from "primereact/image";
import { Dropdown } from "primereact/dropdown";
import { LayoutContext } from "../layout/context/layoutcontext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import AuthService from "../../services/autentication/auth.service";
import { classNames } from "primereact/utils";
import RouteConstant from "../../constants/route.constants";
import UserPasswordService from "../../services/user/user-password.service";
import RoleService from "../../services/role/role-service";
import { ResponseModel } from "../../models/response/response-model";
import { RegistrationMobileSendOtpModel } from "../../models/authentication/registration-mobile-verification.model";
import { AuthenticateModel } from "../../models/authentication/validating-password-model";
import { ForGotPasswordModel } from "../../models/authentication/forgot-password.model";
import Helpers from "../../utils/helpers";
import { Tooltip } from "primereact/tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { Badge } from "primereact/badge";

interface RegisterProps {
    isAdminUser?: boolean;
    hideAdminModal?: (hide: boolean) => void;
}

const Register = ({ isAdminUser = true, hideAdminModal = () => { } }: RegisterProps) => {

    var navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const [isAnyApiError, setAnyApiError] = useState(false);
    const tenantGuid: string = process.env.REACT_APP_TENAT_ID ?? "";
    const [isOtpRequestInProgress, setIsOtpRequestInProgress] = useState(false);
    const [isOtpRequestSent, setIsOtpRequestSent] = useState(false);
    const [isOtpVerified, setIsOtpVerified] = useState(isAdminUser);

    // Referral State
    const [referralCode, setReferralCode] = useState<string | null>(null);
    const [referrerName, setReferrerName] = useState<string>("");
    const [isReferralApplied, setIsReferralApplied] = useState(false);
    const [isCheckingExistingUser, setIsCheckingExistingUser] = useState(false);
    const [existingUserMessage, setExistingUserMessage] = useState("");

    const tooltipText = "Password Tips :\n* Minimum 8 characters.\n* Must contain one special character.\n* Must contain one capital letter.\n* Must contain one number.\nExample: Desiri@123";

    const formik: any = useFormik({
        initialValues: {
            title: { name: "Mr", code: "Mr" },
            contactName: "",
            primaryNumber: "",
            secondaryNumber: "",
            email: "",
            password: "",
            confirmPassword: "",
            agreeTermsAndConditions: false,
            otp: "",
            referredByPhone: "",
            referredByUserId: null
        },
        validate: (data: any) => {
            setAnyApiError(false);

            let errors: any = {};

            if (!data.title) {
                errors.title = t('register_title_is_required')
            }

            if (!data.contactName) {
                errors.contactName = t('register_contact_name_is_required')
            } else if (/^\s/.test(data.contactName)) {
                errors.contactName = "Name should not start with a space";
            } else if (/\s{2,}/.test(data.contactName)) {
                errors.contactName = "Name should not have multiple consecutive spaces";
            }

            if (!data.primaryNumber) {
                errors.primaryNumber = t('register_primary_number_is_required')
            } else if (data.primaryNumber.toString().length !== 10) {
                errors.primaryNumber = "Primary contact number must be exactly 10 digits";
            } else if (!/^[6-9]/.test(data.primaryNumber.toString())) {
                errors.primaryNumber = "Primary contact number must start with 6, 7, 8, or 9";
            }

            if (data.secondaryNumber && data.secondaryNumber.toString().length !== 10) {
                errors.secondaryNumber = "Secondary contact number must be exactly 10 digits";
            } else if (data.secondaryNumber && !/^[6-9]/.test(data.secondaryNumber.toString())) {
                errors.secondaryNumber = "Secondary contact number must start with 6, 7, 8, or 9";
            } else if (data.secondaryNumber && data.primaryNumber && data.secondaryNumber.toString() === data.primaryNumber.toString()) {
                errors.secondaryNumber = "Secondary contact number cannot be the same as primary number";
            }

            if (data.referredByPhone && data.primaryNumber && data.referredByPhone.toString() === data.primaryNumber.toString()) {
                errors.referredByPhone = "You cannot use your own number as a referral code";
            } else if (data.referredByPhone && data.referredByPhone.toString().length !== 10) {
                errors.referredByPhone = "Referral phone number must be 10 digits";
            }

            if (data.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(data.email)) {
                errors.email = "Not a valid email"/*LT*/
            }

            if (!data.password) {
                errors.password = t('register_password_is_required')
            } else if ((!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/i.test(data.password))) {
                errors.password = "Password must contain at least one uppercase, one lowercase, one special character, and one number with a minimum of eight characters";
            }/*LT*/

            if (!data.confirmPassword) {
                errors.confirmPassword = t('register_confirm_password_is_required')
            }

            if (!data.confirmPassword) {
                errors.confirmPassword = t('register_confirm_password_is_required')
            } else if (data.password !== data.confirmPassword) {
                errors.confirmPassword = t('register_passwords_do_not_match');
            }

            if (!data.agreeTermsAndConditions) {
                errors.agreeTermsAndConditions = t('register_please_agree_terms_and_conditions')
            }

            return errors;
        },
        onSubmit: async (data: any) => {

            var user = {
                "tenantGUID": tenantGuid,
                "name": data.contactName,
                "primaryContactNbr": data.primaryNumber?.toString(),
                "secondaryContactNbr": data.secondaryNumber?.toString(),
                "email": data.email,
                "title": data.title.code,
                "password": data.password,
                "otp": data.otp,
                "roleID": 3,
                "referredByPhone": data.referredByPhone?.toString() || null
            };

            var userRegister: ResponseModel = RoleService.isAdmin() ? await AuthService.createUser(user) : await AuthService.register(user);

            const closeDialogModal = () => {
                hideAdminModal(false)
            }

            Helpers.ConsoleLog(userRegister);
            if (userRegister.isSuccess) {
                if (RoleService.isAdmin()) {
                    closeDialogModal()
                }
                else {
                    // Auto-login after successful registration
                    const loginData = {
                        userId: data.primaryNumber?.toString(),
                        password: data.password
                    };

                    const loginResult = await AuthService.login(loginData);

                    if (loginResult) {
                        // Login successful, navigate to home/products
                        navigate(RouteConstant.products);
                    } else {
                        // Login failed, redirect to login page
                        navigate(RouteConstant.login);
                    }
                }
            }
            else {
                setAnyApiError(true);
                return formik.errors["userError"] = userRegister.message;
            }
        }
    });

    const isFormFieldValid = (name: any) => {
        return !!(formik.touched[name] && formik.errors[name]);
    };

    const getFormErrorMessage = (name: any) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    // Prevent leading spaces and multiple consecutive spaces in name fields
    const handleContactNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
        // Prevent leading space
        if (value.startsWith(' ')) {
            value = value.trimStart();
        }
        // Collapse multiple consecutive spaces into one
        value = value.replace(/  +/g, ' ');
        formik.setFieldValue('contactName', value);
        formik.setFieldTouched('contactName', true, false);
    };

    // Referral Logic
    useEffect(() => {
        const ref = searchParams.get('ref');
        if (ref && !isAdminUser) {
            try {
                // Decode the referral code
                const decoded = atob(ref);
                if (decoded.startsWith('REF_')) {
                    setReferralCode(ref);
                    setIsReferralApplied(true);

                    const userIdStr = decoded.replace('REF_', '');
                    const userId = parseInt(userIdStr);
                    if (!isNaN(userId)) {
                        AuthService.getUserById(userId).then(response => {
                            if (response && response.name) {
                                setReferrerName(response.name);
                                // Set the referrer's phone number for submission
                                if (response.primaryContactNbr) {
                                    formik.setFieldValue('referredByPhone', response.primaryContactNbr);
                                }
                            } else {
                                setReferrerName("Your Friend");
                            }
                        }).catch(() => setReferrerName("Your Friend"));
                    }
                }
            } catch (error) {
                console.error('Invalid referral code');
            }
        }
    }, [searchParams, isAdminUser]);

    const checkIfUserExists = async (phoneNumber: string) => {
        if (!phoneNumber || phoneNumber.length < 10) return;

        setIsCheckingExistingUser(true);
        setExistingUserMessage("");

        try {
            const response = await AuthService.checkUserExists(phoneNumber);

            if (response && response.exists) {
                setExistingUserMessage(
                    "This phone number is already registered! Please login instead."
                );
                setIsReferralApplied(false); // Disable referral
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error checking user existence');
            return false;
        } finally {
            setIsCheckingExistingUser(false);
        }
    };

    const verifyOTP = async (event: any) => {
        if (formik.values.otp) {
            const data: ForGotPasswordModel = {
                phoneNumber: formik.values.primaryNumber?.toString()
            }
            setIsOtpRequestInProgress(true);
            var authenticateModel: AuthenticateModel = { mobileNumber: formik.values.primaryNumber?.toString(), otp: formik.values.otp };

            let response = await UserPasswordService.forgotPasswordVerifyOTP(authenticateModel);
            if (response?.isSuccess) {
                setIsOtpRequestInProgress(false);
                setIsOtpVerified(true);
            }
            else {
                if (!response?.isSuccess) {
                    formik.errors["userError"] = response.message;
                }
                setIsOtpRequestInProgress(false);
                setIsOtpVerified(false);
            }
        } else {
            //for validating form originally it won't send the form
            formik.submitForm();
        }
    }

    const sendOTP = async (event: any) => {
        if (formik.values.primaryNumber) {
            const data: RegistrationMobileSendOtpModel = {
                mobileNo: formik.values.primaryNumber?.toString(),
                tenantGUID: tenantGuid
            }
            setIsOtpRequestInProgress(true);
            let response = await UserPasswordService.sendUserRegistrationOTP(data);
            if (response?.isSuccess) {
                setIsOtpRequestInProgress(false);
                setIsOtpRequestSent(true);
            }
            else {
                if (!response?.isSuccess) {
                    formik.errors["userError"] = response.message;
                }
                setIsOtpRequestInProgress(false);
                setIsOtpRequestSent(false);
            }
        } else {
            //for validating form originally it won't send the form
            formik.submitForm();
        }

    }

    const { layoutConfig } = useContext(LayoutContext);
    const dropdownItems = [
        { name: "Mr", code: "Mr" },
        { name: "Miss", code: "Miss" }
    ];

    const containerClassName = classNames(
        "surface-ground flex min-h-screen min-w-screen overflow-hidden",
        { "p-input-filled": layoutConfig.inputStyle === "filled" }
    );
    const isAdminConatinerClassName = classNames("p-input-filled");

    const isRegistred = () => {
        if (isOtpVerified) {
            if (isAdminUser) {
                return false;
            }
            return true;
        }

        return false;
    }
    return (
        <>
            <style>
                {`
                    @keyframes slideUp {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    @keyframes pulse {
                        0%, 100% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.05); opacity: 0.9; }
                    }

                    .login-panel {
                        width: 100%;
                        max-width: 100% !important;
                        margin: 0;
                    }

                    .premium-card {
                        background: #ffffff;
                        border-radius: 0;
                        box-shadow: none;
                        overflow: hidden;
                        transition: all 0.3s ease;
                        animation: slideUp 0.6s ease-out;
                        width: 100%;
                    }

                    .premium-header {
                        background: linear-gradient(135deg, #00703c 0%, #00a859 100%);
                        padding: 1.5rem;
                        text-align: center;
                        width: 100%;
                        border-radius: 0;
                    }

                    .premium-header h5 {
                        color: #ffffff !important;
                        margin: 0 !important;
                        font-weight: 700;
                        letter-spacing: 1.5px;
                        text-transform: uppercase;
                        font-size: 1.25rem;
                        text-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    }

                    .p-inputtext:focus, .p-password-input:focus {
                        box-shadow: 0 0 0 3px rgba(0, 168, 89, 0.15) !important;
                        border-color: #00a859 !important;
                    }

                    .label-semi-bold {
                        font-weight: 600;
                        color: #374151;
                        margin-bottom: 0.5rem;
                        display: block;
                        font-size: 0.875rem;
                    }

                    .required {
                        color: #ef4444;
                        margin-left: 2px;
                    }

                    .p-error {
                        font-size: 0.75rem;
                        margin-top: 0.25rem;
                        color: #ef4444;
                        font-weight: 500;
                    }

                    .field {
                        margin-bottom: 1.25rem !important;
                    }

                    .animation-pulse {
                        animation: pulse 2s infinite ease-in-out;
                    }

                    @media (max-width: 768px) {
                        .login-panel {
                            padding: 0;
                            max-width: 100% !important;
                        }
                        .premium-card {
                            border-radius: 0;
                        }
                        .premium-header {
                            padding: 1.25rem 1rem;
                        }
                        .premium-header h5 {
                            font-size: 1.1rem;
                        }
                    }
                `}
            </style>
            <div className={RoleService.isAdmin() ? isAdminConatinerClassName : containerClassName}>
                <div className={`${!RoleService.isAdmin() ? 'flex flex-1 flex-row grid' : ""}`}>
                    {!isAdminUser && <div className="col-12 md:col-6 lg:col-6 p-5 flex flex-column justify-content-center flex-wrap cursor-pointer" onClick={() => navigate(RouteConstant.products)} >
                        <div className="flex flex-1 align-items-center justify-content-center">
                            <Image src={process.env.PUBLIC_URL + "/assests/images/logo.png"} className="flex flex-wrap align-items-center justify-content-center" width="25%" />
                        </div>
                        <div className="flex flex-1 align-items-center justify-content-center">
                            <Image src={process.env.PUBLIC_URL + "/assests/images/form2me-fruits.png"} className="flex flex-wrap align-items-center justify-content-center" width="80%" />
                        </div>
                    </div>}

                    <div className={`${!RoleService.isAdmin() ? 'col-12 md:col-6 lg:col-6  flex align-items-stretch flex-wrap' : ""}`}>
                        <div className="flex flex-1 align-items-center justify-content-center p-0">
                            <div className="login-panel" >
                                <form onSubmit={formik.handleSubmit} className="premium-card">
                                    <div className="premium-header">
                                        <h5 className="m-0">{t('register_create_your_account')}</h5>
                                    </div>
                                    <div className="p-4">
                                        <div className="p-fluid formgrid grid">
                                        <div className="field col-12 mb-2">
                                            <label htmlFor="primaryNumber" className="label-semi-bold">{t('register_primary_contact_number')}<span className="required">*</span></label>
                                            <span className="p-input-icon-left">
                                                <i className="pi pi-solid pi-phone" />
                                                <InputText
                                                    keyfilter="int"
                                                    maxLength={10}
                                                    disabled={isRegistred()}
                                                    id="primaryNumber"
                                                    name="primaryNumber"
                                                    value={formik.values.primaryNumber}
                                                    onChange={(e) => {
                                                        if (e.target.value.length <= 10) {
                                                            formik.handleChange(e);
                                                        }
                                                        setExistingUserMessage("");
                                                    }}
                                                    onBlur={(e) => {
                                                        checkIfUserExists(e.target.value);
                                                        formik.handleBlur(e);
                                                    }}
                                                    className={classNames({ 'p-invalid': isFormFieldValid('primaryNumber') })}
                                                    placeholder={t('register_enter_your_contact_number')}
                                                />
                                            </span>
                                            {getFormErrorMessage('primaryNumber')}

                                            {/* Existing User Warning */}
                                            {existingUserMessage && (
                                                <div className="p-3 mt-2" style={{
                                                    background: '#fef2f2',
                                                    border: '2px solid #ef4444',
                                                    borderRadius: '8px',
                                                    color: '#dc2626'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                                        <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.25rem' }}></i>
                                                        <strong>{existingUserMessage}</strong>
                                                    </div>
                                                    <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}>
                                                        You cannot use a referral link because you're already a customer.
                                                    </p>
                                                    {/* <Button
                                                        label="Go to Login"
                                                        icon="pi pi-sign-in"
                                                        size="small"
                                                        type="button"
                                                        onClick={() => navigate(RouteConstant.login)}
                                                        style={{
                                                            background: '#ef4444',
                                                            borderColor: '#ef4444',
                                                            marginTop: '0.5rem'
                                                        }}
                                                    /> */}
                                                </div>
                                            )}
                                        </div>
                                        {isOtpRequestSent && !isOtpVerified &&
                                            <div className="field col-12  mb-2" >
                                                <label htmlFor="lastname2" className="label-semi-bold">{t('forgotpassword_enter_your_otp_code')}<span className="required">*</span></label>
                                                <span className="p-input-icon-right">
                                                    <InputText type="text" name="otp" id="otp" onChange={formik.handleChange} placeholder={t('forgotpassword_otp')} className={classNames({ 'p-invalid': formik.errors.otp })} />
                                                    <i className="	pi pi-verified" />
                                                </span>
                                                {getFormErrorMessage('otp')}
                                            </div>
                                        }
                                        {isOtpVerified &&
                                            <>
                                                <div className="col-12">

                                                    <div className="grid">
                                                        <div className="col-12 md:col-6 lg:col-3 mb-2">
                                                            <div className="field">
                                                                <label htmlFor="title" className="label-semi-bold">{t('register_title')}<span className="required">*</span></label>
                                                                <Dropdown
                                                                    id="title"
                                                                    name="title"
                                                                    className={classNames({ 'p-invalid': formik.errors.title })}
                                                                    value={formik.values.title}
                                                                    onChange={formik.handleChange}
                                                                    options={dropdownItems}
                                                                    optionLabel="name"
                                                                    placeholder="Mr"
                                                                ></Dropdown>
                                                                {getFormErrorMessage('title')}
                                                            </div>
                                                        </div>
                                                        <div className="col-12 md:col-6 lg:col-9 mb-2">
                                                            <div className="field">
                                                                <label htmlFor="contactName" className="label-semi-bold">{t('register_name')}<span className="required">*</span></label>
                                                                <span className="p-input-icon-left">
                                                                    <i className="pi pi-user" />
                                                                    <InputText
                                                                        type="text"
                                                                        id="contactName"
                                                                        name="contactName"
                                                                        value={formik.values.contactName}
                                                                        onChange={handleContactNameChange}
                                                                        onBlur={formik.handleBlur}
                                                                        className={classNames({ 'p-invalid': isFormFieldValid('contactName') })}
                                                                        placeholder={t('register_enter_your_name')}
                                                                    />
                                                                </span>
                                                                {getFormErrorMessage('contactName')}
                                                            </div>
                                                        </div>
                                                    </div>

                                                </div>
                                                <div className="field col-12 mb-2">
                                                    <label htmlFor="secondaryNumber" className="label-semi-bold">{t('register_secondary_contact_number')}</label>
                                                    <span className="p-input-icon-left">
                                                        <i className="pi pi-solid pi-phone" />
                                                        <InputText
                                                            keyfilter="int"
                                                            maxLength={10}
                                                            name="secondaryNumber"
                                                            id="secondaryNumber"
                                                            value={formik.values.secondaryNumber}
                                                            onChange={(e) => {
                                                                if (e.target.value.length <= 10) {
                                                                    formik.handleChange(e);
                                                                }
                                                            }}
                                                            onBlur={formik.handleBlur}
                                                            className={classNames({ 'p-invalid': isFormFieldValid('secondaryNumber') })}
                                                            placeholder={t('register_enter_your_contact_number')}
                                                        />
                                                    </span>
                                                    {getFormErrorMessage('secondaryNumber')}
                                                </div>
                                                <div className="field col-12 mb-2">
                                                    <label htmlFor="email" className="label-semi-bold">{t('register_email')}</label>
                                                    <span className="p-input-icon-left">
                                                        <i className="pi pi-envelope" />
                                                        <InputText type="text" name="email" id="email" onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('email') })} placeholder={t('register_enter_your_email')} />
                                                    </span>
                                                    {getFormErrorMessage('email')}
                                                </div>
                                                {/* Referral Section */}
                                                <div className="field col-12 mb-3" style={{
                                                    background: isReferralApplied ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' : '#f8fafc',
                                                    padding: '1.25rem',
                                                    borderRadius: '12px',
                                                    border: isReferralApplied ? '2px dashed #059669' : '1px dashed #cbd5e1',
                                                    position: 'relative',
                                                    transition: 'all 0.3s ease'
                                                }}>
                                                    {isReferralApplied ? (
                                                        <div className="flex align-items-center gap-3">
                                                            <div className="flex align-items-center justify-content-center bg-white border-circle"
                                                                style={{ width: '48px', height: '48px', boxShadow: '0 2px 8px rgba(5,150,105,0.1)' }}>
                                                                <i className="pi pi-check-circle text-green-600 text-2xl"></i>
                                                            </div>
                                                            <div className="flex-1">
                                                                <p className="m-0 text-green-800 font-semibold">Referred by {referrerName}</p>
                                                                <p className="m-0 text-green-600" style={{ fontSize: '0.85rem' }}>Referral Applied</p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="flex align-items-center justify-content-between mb-2">
                                                                <div className="flex align-items-center gap-2">
                                                                    <i className="pi pi-gift text-green-600 text-xl animation-pulse"></i>
                                                                    <label htmlFor="referredByPhone" className="label-semi-bold m-0 text-gray-700">
                                                                        Got a Referral Code?
                                                                    </label>
                                                                </div>
                                                                <span className="text-xs font-semibold px-2 py-1 bg-gray-200 text-gray-600 border-round">Optional</span>
                                                            </div>

                                                            <span className="p-input-icon-left w-full">
                                                                <i className="pi pi-phone text-gray-500" />
                                                                <InputText
                                                                    keyfilter="int"
                                                                    name="referredByPhone"
                                                                    id="referredByPhone"
                                                                    value={formik.values.referredByPhone}
                                                                    onChange={formik.handleChange}
                                                                    placeholder="Enter referrer's phone number"
                                                                    className={classNames("w-full", { 'p-invalid': isFormFieldValid('referredByPhone') })}
                                                                    style={{ borderRadius: '8px' }}
                                                                />
                                                            </span>
                                                            {getFormErrorMessage('referredByPhone')}
                                                            <small className="block mt-2 text-gray-500 text-xs">
                                                                Enter the phone number of the person who referred you.
                                                            </small>
                                                        </>
                                                    )}
                                                </div>
                                                <div className="field col-12 mb-2">
                                                    <label htmlFor="password" className="label-semi-bold" style={{ width: "100%" }}>
                                                        {t('register_create_password')}
                                                        <span className="required">*</span>
                                                        <span className="mr-2" style={{ float: "right" }}>
                                                            <Tooltip target=".custom-target-icon" position="top">
                                                                {tooltipText}
                                                            </Tooltip>
                                                            <i className="custom-target-icon fas fa-info-circle warning"
                                                                style={{ fontSize: '1rem', color: 'yellowgreen', cursor: 'pointer' }}>
                                                            </i>
                                                        </span>
                                                    </label>
                                                    <Password
                                                        inputId="password"
                                                        name="password"
                                                        className={classNames('w-full', { 'p-invalid': isFormFieldValid('password') })}
                                                        inputClassName="w-full"
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        placeholder={t('register_create_password')}
                                                        toggleMask
                                                    />
                                                    {getFormErrorMessage('password')}
                                                </div>
                                                <div className="field col-12 mb-2">
                                                    <label htmlFor="confirmPassword" className="label-semi-bold">{t('register_confirm_password')}<span className="required">*</span></label>
                                                    <Password
                                                        inputId="confirmPassword"
                                                        name="confirmPassword"
                                                        className={classNames('w-full', { 'p-invalid': isFormFieldValid('confirmPassword') })}
                                                        inputClassName="w-full"
                                                        feedback={false}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        placeholder={t('register_confirm_password')}
                                                        toggleMask
                                                    />
                                                    {getFormErrorMessage('confirmPassword')}
                                                </div>
                                                <div className="field col-12 mb-2">
                                                    <div className="field-checkbox col-12">
                                                        <Checkbox
                                                            id="agreeTermsAndConditions"
                                                            name="agreeTermsAndConditions"
                                                            onChange={(e) => {
                                                                formik.setFieldValue('agreeTermsAndConditions', e.checked);
                                                            }}
                                                            checked={formik.values.agreeTermsAndConditions}
                                                        />
                                                        <label htmlFor="agreeTermsAndConditions">{t('register_i_agree_to')} <span className="link-text cursor-pointer" onClick={() => { window.open(RouteConstant.registerTermsAndConditions, '_blank', 'rel=noopener noreferrer') }}>{t('register_all_terms_conditions')}</span><span className="required">*</span></label>
                                                    </div>
                                                    {getFormErrorMessage('agreeTermsAndConditions')}
                                                </div>
                                            </>
                                        }

                                        {
                                            formik.errors['userError'] &&
                                            <div className="col-12 mb-2" >
                                                <small className="p-error">{formik.errors['userError']}</small>
                                            </div>
                                        }
                                        {
                                            !isOtpRequestSent && !isOtpVerified &&
                                            <div className="col-12 mb-2" >
                                                <Button type="button" onClick={sendOTP} label={isOtpRequestInProgress ? "sending.." : "Send OTP"} loading={isOtpRequestInProgress}  > </Button>{/*LT*/}
                                            </div>
                                        }
                                        {
                                            isOtpRequestSent && !isOtpVerified &&
                                            <div className="col-12 mb-2" >
                                                <Button type="button" onClick={verifyOTP} label={isOtpRequestInProgress ? "Verifing.." : "Verify OTP & Continue.."} loading={isOtpRequestInProgress}  > </Button>{/*LT*/}
                                            </div>
                                        }
                                        {
                                            isOtpVerified &&
                                            <>
                                                <div className="col-12 mt-3" >
                                                    <Button 
                                                        type="submit" 
                                                        label={t('register_sign_up')} 
                                                        className="p-button-raised"
                                                        style={{ background: 'linear-gradient(135deg, #00703c 0%, #00a859 100%)', border: 'none', padding: '0.75rem' }}
                                                    />
                                                    <div className="dont-have-anaccount mt-3">
                                                        {t('register_do_not_have_an_account')}? 
                                                        <span className="primary-text-color ml-1 font-bold hover:underline" onClick={() => { AuthService.logout(); navigate(RouteConstant.login); }} style={{ color: '#00a859' }}>
                                                            {t('register_login')}
                                                        </span>
                                                    </div>
                                                </div>
                                            </>
                                        }

                                    </div>
                                </div>
                            </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Register;
